import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  ArrowLeft, Plus, X, DollarSign, Save, AlertTriangle, Upload, Image, Film,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RecipeForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const preselectedLoyverseId = searchParams.get('loyverse_id');
  const preselectedName = searchParams.get('name');

  const [stockItems, setStockItems] = useState([]);
  const [menuProducts, setMenuProducts] = useState([]);
  const [benchmarkMargins, setBenchmarkMargins] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    loyverse_item_id: preselectedLoyverseId || '',
    name: preselectedName || '',
    description: '',
    category: '',
    ingredients: [],
    method: [{ text: '', media_url: null, media_type: null }],
    image_url: '',
    benchmark_cost_percentage: 30,
    selling_price_incl_vat: 0,
    markup_percentage: 0,
    portions: 1,
  });

  const [newIngredient, setNewIngredient] = useState({ stock_item_id: '', quantity: 0 });
  const [uploading, setUploading] = useState(null); // 'recipe' | 'method-N' | null

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const promises = [
        axios.get(`${API}/stock-items`, { withCredentials: true }),
        axios.get(`${API}/menu-products`, { withCredentials: true }),
        axios.get(`${API}/settings/benchmark-margins`, { withCredentials: true }).catch(() => ({ data: { restaurant: 30, retail: 40 } })),
      ];
      if (isEditing) {
        promises.push(axios.get(`${API}/recipes/${id}`, { withCredentials: true }));
      }

      const results = await Promise.all(promises);
      setStockItems(results[0].data);
      setMenuProducts(results[1].data);
      setBenchmarkMargins(results[2].data);

      if (isEditing && results[3]) {
        const recipe = results[3].data;
        setFormData({
          loyverse_item_id: recipe.loyverse_item_id || recipe.linked_product_id || '',
          name: recipe.name || '',
          description: recipe.description || '',
          category: recipe.category || '',
          ingredients: (recipe.ingredients || []).map(ing => ({
            stock_item_id: ing.stock_item_id,
            stock_item_name: ing.name || ing.stock_item_name || '',
            quantity: ing.quantity,
            unit: ing.unit || '',
            cost_price: ing.cost_per_unit || ing.cost_price || 0,
          })),
          method: recipe.method?.length > 0
            ? recipe.method.map(s => typeof s === 'string' ? { text: s, media_url: null, media_type: null } : { text: s.text || '', media_url: s.media_url || null, media_type: s.media_type || null })
            : [{ text: '', media_url: null, media_type: null }],
          image_url: recipe.image_url || '',
          benchmark_cost_percentage: recipe.benchmark_cost_percentage || 30,
          selling_price_incl_vat: recipe.selling_price_incl_vat || 0,
          markup_percentage: recipe.markup_percentage || 0,
          portions: recipe.portions || 1,
        });
      }
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const totalCost = formData.ingredients.reduce(
    (s, i) => s + (i.cost_price || 0) * (i.quantity || 0), 0
  );

  const sellingExclVat = formData.selling_price_incl_vat > 0
    ? formData.selling_price_incl_vat / 1.15
    : 0;

  const costPct = sellingExclVat > 0
    ? (totalCost / sellingExclVat) * 100
    : 0;

  const isOverBenchmark = costPct > formData.benchmark_cost_percentage && sellingExclVat > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.loyverse_item_id && !isEditing) {
      toast.error('Please select a menu product');
      return;
    }
    if (formData.ingredients.length === 0) {
      toast.error('Add at least one ingredient');
      return;
    }

    setSaving(true);
    const payload = {
      name: formData.name,
      description: formData.description,
      category: formData.category,
      method: (formData.method || [])
        .filter(s => (s.text || '').trim() || s.media_url)
        .map(s => ({ text: s.text || '', media_url: s.media_url || null, media_type: s.media_type || null })),
      portions: formData.portions || 1,
      selling_price_incl_vat: parseFloat(formData.selling_price_incl_vat) || 0,
      markup_percentage: parseFloat(formData.markup_percentage) || 0,
      benchmark_cost_percentage: parseFloat(formData.benchmark_cost_percentage) || 30,
      ingredients: formData.ingredients.map(ing => ({
        stock_item_id: ing.stock_item_id,
        name: ing.stock_item_name || ing.name,
        quantity: ing.quantity,
        unit: ing.unit,
      })),
      linked_product_id: formData.loyverse_item_id || null,
      image_url: formData.image_url || null,
    };

    try {
      if (isEditing) {
        await axios.put(`${API}/recipes/${id}`, payload, { withCredentials: true });
        toast.success('Recipe updated');
        navigate(`/recipes/${id}`);
      } else {
        const res = await axios.post(`${API}/recipes`, payload, { withCredentials: true });
        toast.success('Recipe created');
        navigate(`/recipes/${res.data.recipe_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save recipe');
    } finally {
      setSaving(false);
    }
  };

  const addIngredient = () => {
    if (!newIngredient.stock_item_id || !newIngredient.quantity) return;
    const item = stockItems.find(i => i.stock_item_id === newIngredient.stock_item_id);
    if (!item) return;
    const costPerRecipeUnit = (item.cost_price || 0) / ((item.order_unit_qty || 1) * (item.recipe_unit_qty || 1));
    setFormData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, {
        stock_item_id: item.stock_item_id,
        stock_item_name: item.name,
        quantity: newIngredient.quantity,
        unit: item.recipe_unit || item.unit,
        cost_price: costPerRecipeUnit,
      }],
    }));
    setNewIngredient({ stock_item_id: '', quantity: 0 });
  };

  const removeIngredient = (index) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index),
    }));
  };

  const addMethodStep = () => {
    setFormData(prev => ({ ...prev, method: [...prev.method, { text: '', media_url: null, media_type: null }] }));
  };

  const updateMethodStep = (index, value) => {
    setFormData(prev => ({
      ...prev,
      method: prev.method.map((s, i) => (i === index ? { ...s, text: value } : s)),
    }));
  };

  const removeMethodStep = (index) => {
    if (formData.method.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      method: prev.method.filter((_, i) => i !== index),
    }));
  };

  const uploadFile = async (file, target) => {
    setUploading(target);
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await axios.post(`${API}/upload`, form, { withCredentials: true });
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
      return null;
    } finally {
      setUploading(null);
    }
  };

  const handleRecipeImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = await uploadFile(file, 'recipe');
    if (result) {
      setFormData(prev => ({ ...prev, image_url: result.url }));
    }
  };

  const handleMethodMediaUpload = async (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = await uploadFile(file, `method-${index}`);
    if (result) {
      const mediaType = file.type.startsWith('video/') ? 'video' : 'image';
      setFormData(prev => ({
        ...prev,
        method: prev.method.map((s, i) => i === index ? { ...s, media_url: result.url, media_type: mediaType } : s),
      }));
    }
  };

  const removeMethodMedia = (index) => {
    setFormData(prev => ({
      ...prev,
      method: prev.method.map((s, i) => i === index ? { ...s, media_url: null, media_type: null } : s),
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const linkedProduct = menuProducts.find(p => p.loyverse_id === formData.loyverse_item_id);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="recipe-form-page">
      {/* Header */}
      <form onSubmit={handleSubmit}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/recipes">
              <Button type="button" variant="ghost" size="icon" data-testid="recipe-form-back">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {isEditing ? 'Edit Recipe' : 'New Recipe'}
              </h1>
              {linkedProduct && (
                <p className="text-sm text-muted-foreground">
                  Linked to: {linkedProduct.name}
                </p>
              )}
            </div>
          </div>
          <Button type="submit" disabled={saving} data-testid="recipe-form-save">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : isEditing ? 'Update Recipe' : 'Create Recipe'}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Main form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basics */}
            <Card>
              <CardHeader>
                <CardTitle>Recipe Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!isEditing && (
                  <div className="space-y-2">
                    <Label>Link to Menu Product *</Label>
                    <Select
                      value={formData.loyverse_item_id}
                      onValueChange={(value) => {
                        const product = menuProducts.find(p => p.loyverse_id === value);
                        setFormData({
                          ...formData,
                          loyverse_item_id: value,
                          name: product?.name || formData.name,
                          image_url: product?.image_url || formData.image_url,
                        });
                      }}
                    >
                      <SelectTrigger data-testid="recipe-form-product-select">
                        <SelectValue placeholder="Select a menu product" />
                      </SelectTrigger>
                      <SelectContent>
                        {menuProducts
                          .filter(p => !p.has_recipe || p.loyverse_id === formData.loyverse_item_id)
                          .map(p => (
                            <SelectItem key={p.loyverse_id} value={p.loyverse_id}>
                              {p.name} {p.loyverse_price ? `(R${p.loyverse_price.toFixed(2)})` : ''}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Recipe Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Cappuccino"
                      required
                      data-testid="recipe-form-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={formData.category || 'none'}
                      onValueChange={(val) => {
                        const cat = val === 'none' ? '' : val;
                        setFormData({
                          ...formData,
                          category: cat,
                          benchmark_cost_percentage: benchmarkMargins[cat.toLowerCase()] || formData.benchmark_cost_percentage,
                        });
                      }}
                    >
                      <SelectTrigger data-testid="recipe-form-category">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Category</SelectItem>
                        <SelectItem value="Restaurant">Restaurant ({benchmarkMargins.restaurant || 30}%)</SelectItem>
                        <SelectItem value="Retail">Retail ({benchmarkMargins.retail || 40}%)</SelectItem>
                        {Object.keys(benchmarkMargins)
                          .filter(k => !['restaurant', 'retail'].includes(k))
                          .map(k => (
                            <SelectItem key={k} value={k.charAt(0).toUpperCase() + k.slice(1)}>
                              {k.charAt(0).toUpperCase() + k.slice(1)} ({benchmarkMargins[k]}%)
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description..."
                    rows={2}
                    data-testid="recipe-form-description"
                  />
                </div>

                {/* Recipe Image */}
                <div className="space-y-2">
                  <Label>Recipe Image</Label>
                  <div className="flex items-center gap-4">
                    {formData.image_url ? (
                      <div className="relative">
                        <img
                          src={formData.image_url}
                          alt="Recipe"
                          className="w-24 h-24 rounded-lg object-cover border"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="absolute -top-2 -right-2 h-6 w-6"
                          onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <label className="flex items-center gap-2 px-4 py-2 border border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors" data-testid="recipe-image-upload">
                        <Upload className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {uploading === 'recipe' ? 'Uploading...' : 'Upload image'}
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleRecipeImageUpload}
                          disabled={uploading === 'recipe'}
                        />
                      </label>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Portions</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.portions}
                      onChange={(e) => setFormData({ ...formData, portions: parseInt(e.target.value) || 1 })}
                      data-testid="recipe-form-portions"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Ingredients */}
            <Card>
              <CardHeader>
                <CardTitle>Ingredients</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add ingredient */}
                {(() => {
                  const selectedItem = stockItems.find(i => i.stock_item_id === newIngredient.stock_item_id);
                  const previewCost = selectedItem ? ((selectedItem.cost_price || 0) / ((selectedItem.order_unit_qty || 1) * (selectedItem.recipe_unit_qty || 1))) * (newIngredient.quantity || 0) : 0;
                  const displayUnit = selectedItem?.recipe_unit || selectedItem?.unit;
                  return (
                    <div className="p-3 border rounded-lg bg-muted/30 space-y-3">
                      <div className="flex gap-2 items-end">
                        <div className="flex-1 space-y-1">
                          <Label className="text-xs">Stock Item</Label>
                          <Select
                            value={newIngredient.stock_item_id}
                            onValueChange={(v) => setNewIngredient({ ...newIngredient, stock_item_id: v })}
                          >
                            <SelectTrigger data-testid="recipe-form-ingredient-select">
                              <SelectValue placeholder="Select stock item" />
                            </SelectTrigger>
                            <SelectContent>
                              {stockItems.map(item => {
                                const recipeUnit = item.recipe_unit || item.unit;
                                const costPerRecipeUnit = (item.cost_price || 0) / ((item.order_unit_qty || 1) * (item.recipe_unit_qty || 1));
                                return (
                                  <SelectItem key={item.stock_item_id} value={item.stock_item_id}>
                                    {item.name} — {recipeUnit} @ R{costPerRecipeUnit.toFixed(2)}/{recipeUnit}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-32 space-y-1">
                          <Label className="text-xs">
                            Qty {selectedItem ? <span className="text-primary font-medium">({displayUnit})</span> : ''}
                          </Label>
                          <Input
                            type="number"
                            step="0.001"
                            min="0"
                            value={newIngredient.quantity || ''}
                            onChange={(e) => setNewIngredient({ ...newIngredient, quantity: parseFloat(e.target.value) || 0 })}
                            placeholder="0"
                            data-testid="recipe-form-ingredient-qty"
                          />
                        </div>
                        <Button type="button" onClick={addIngredient} disabled={!selectedItem || !newIngredient.quantity} data-testid="recipe-form-add-ingredient">
                          <Plus className="w-4 h-4 mr-1" /> Add
                        </Button>
                      </div>
                      {selectedItem && (
                        <div className="flex items-center gap-4 text-sm" data-testid="ingredient-preview">
                          <span className="text-muted-foreground">
                            Unit cost: <span className="font-medium text-foreground">R{((selectedItem.cost_price || 0) / ((selectedItem.order_unit_qty || 1) * (selectedItem.recipe_unit_qty || 1))).toFixed(2)}</span> per {displayUnit}
                          </span>
                          {newIngredient.quantity > 0 && (
                            <>
                              <span className="text-muted-foreground">|</span>
                              <span className="text-muted-foreground">
                                Line cost: <span className="font-semibold text-primary">R{previewCost.toFixed(2)}</span>
                              </span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {formData.ingredients.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Item</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Unit Cost</TableHead>
                          <TableHead className="text-right">Line Cost</TableHead>
                          <TableHead className="w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {formData.ingredients.map((ing, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">
                              {ing.stock_item_name || ing.name || 'Unknown'}
                              <span className="text-xs text-muted-foreground ml-1">({ing.unit})</span>
                            </TableCell>
                            <TableCell className="text-right">
                              <Input
                                type="number"
                                step="0.001"
                                min="0"
                                value={ing.quantity}
                                onChange={(e) => {
                                  const qty = parseFloat(e.target.value) || 0;
                                  setFormData(prev => ({
                                    ...prev,
                                    ingredients: prev.ingredients.map((item, i) =>
                                      i === idx ? { ...item, quantity: qty } : item
                                    ),
                                  }));
                                }}
                                className="w-20 text-right inline-block ml-auto"
                                data-testid={`ingredient-qty-${idx}`}
                              />
                            </TableCell>
                            <TableCell className="text-right">
                              R{(ing.cost_price || 0).toFixed(2)}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              R{((ing.cost_price || 0) * ing.quantity).toFixed(2)}
                            </TableCell>
                            <TableCell>
                              <Button type="button" variant="ghost" size="icon" onClick={() => removeIngredient(idx)}>
                                <X className="w-4 h-4 text-destructive" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-muted/50 font-semibold">
                          <TableCell colSpan={3} className="text-right">Total Recipe Cost:</TableCell>
                          <TableCell className="text-right text-primary">R{totalCost.toFixed(2)}</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                )}

                {formData.ingredients.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No ingredients added yet. Select a stock item and quantity above.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Method */}
            <Card>
              <CardHeader>
                <CardTitle>Method</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {formData.method.map((step, idx) => (
                  <div key={idx} className="border rounded-lg p-3 space-y-2">
                    <div className="flex gap-2 items-center">
                      <span className="text-sm text-muted-foreground w-6 text-right flex-shrink-0 font-medium">
                        {idx + 1}.
                      </span>
                      <Input
                        value={step.text || ''}
                        onChange={(e) => updateMethodStep(idx, e.target.value)}
                        placeholder={`Step ${idx + 1}...`}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeMethodStep(idx)}
                        disabled={formData.method.length <= 1}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Media preview or upload */}
                    <div className="ml-8">
                      {step.media_url ? (
                        <div className="relative inline-block">
                          {step.media_type === 'video' ? (
                            <video
                              src={step.media_url}
                              className="w-40 h-28 rounded object-cover border"
                              controls
                            />
                          ) : (
                            <img
                              src={step.media_url}
                              alt={`Step ${idx + 1}`}
                              className="w-40 h-28 rounded object-cover border"
                            />
                          )}
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute -top-2 -right-2 h-5 w-5"
                            onClick={() => removeMethodMedia(idx)}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-dashed rounded cursor-pointer hover:bg-muted/50 transition-colors text-muted-foreground">
                          {uploading === `method-${idx}` ? (
                            <span>Uploading...</span>
                          ) : (
                            <>
                              <Image className="w-3 h-3" />
                              <Film className="w-3 h-3" />
                              <span>Add image or video</span>
                            </>
                          )}
                          <input
                            type="file"
                            accept="image/*,video/*"
                            className="hidden"
                            onChange={(e) => handleMethodMediaUpload(e, idx)}
                            disabled={!!uploading}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addMethodStep}>
                  <Plus className="w-3 h-3 mr-1" />
                  Add Step
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right sidebar: Pricing */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Pricing
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm">Selling Price (incl VAT)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.selling_price_incl_vat}
                    onChange={(e) => setFormData({ ...formData, selling_price_incl_vat: parseFloat(e.target.value) || 0 })}
                    data-testid="recipe-form-selling-price"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Target Cost %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={formData.benchmark_cost_percentage}
                    onChange={(e) => setFormData({ ...formData, benchmark_cost_percentage: parseFloat(e.target.value) || 30 })}
                    data-testid="recipe-form-benchmark"
                  />
                </div>

                <Separator />

                {/* Live cost summary */}
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Recipe Cost</span>
                    <span className="font-bold text-primary">R{totalCost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Sell (incl VAT)</span>
                    <span className="font-medium">R{(formData.selling_price_incl_vat || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Sell (excl VAT)</span>
                    <span className="font-medium">R{sellingExclVat.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Ingredients</span>
                    <span className="font-medium">{formData.ingredients.length}</span>
                  </div>

                  <Separator />

                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Actual Cost %</span>
                    <Badge variant={isOverBenchmark ? 'destructive' : 'secondary'}>
                      {costPct.toFixed(1)}%
                    </Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Target</span>
                    <span>{formData.benchmark_cost_percentage}%</span>
                  </div>

                  {isOverBenchmark && (
                    <div className="flex items-center gap-2 p-2 bg-destructive/10 rounded text-sm text-destructive">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      Over benchmark by {(costPct - formData.benchmark_cost_percentage).toFixed(1)}%
                    </div>
                  )}

                  {sellingExclVat > 0 && !isOverBenchmark && (
                    <div className="p-2 bg-green-50 rounded text-sm text-green-700">
                      Within target cost percentage
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}
